import { cn } from 'lib/utils'
import { Loader2, Plus } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { WeeklyDayBucket, WeeklyQueueItem } from '../../store/weekly-queue-store'
import { QueuedPostCard } from './QueuedPostCard'

interface WeekGridProps {
  days: WeeklyDayBucket[]
  items: WeeklyQueueItem[]
  /**
   * The active autopilot plan's selected platforms. Used to preview the full
   * publish set on a still-generating card (whose confirmedPlatforms isn't
   * stamped yet). Empty when there's no active plan.
   */
  planChannels: string[]
  /**
   * Creative IDs that just flipped `generating → done` on the latest poll.
   * Drives the platform-icon center→corner settle animation on the card that
   * just finished (the cell remounts the card on completion in review mode, so
   * the card needs to be told to play it on mount).
   */
  justGeneratedIds: Record<string, true>
  optimisticDrafts: Record<string, number>
  /** Creative whose PostModal payload is currently loading (per-card veil). */
  openingId: string | null
  /** Whole-card click → open PostModal. */
  onOpenPost: (creativeId: string) => void
  /** ••• Edit → composer. */
  onEdit: (creativeId: string, channel: string) => void
  /** ••• Unschedule → move a scheduled post back to a draft. */
  onUnschedule: (creativeId: string) => void
  onAddPost: (date: string) => Promise<void> | void
  /** Approve a review-mode post so it publishes at its scheduled time. */
  onApprove?: (creativeId: string) => Promise<void> | void
  /** Delete a post from the week. Drives the card's ••• menu. */
  onDelete?: (creativeId: string, channel: string) => Promise<void> | void
  className?: string
}

// Soft fade-out at the bottom so cards visibly trim at the fold instead of
// hard-cutting. Tailwind has no mask-image utility, so set the property inline.
const MASK_STYLE: React.CSSProperties = {
  WebkitMaskImage: 'linear-gradient(to bottom, black 0, black calc(100% - 40px), transparent 100%)',
  maskImage: 'linear-gradient(to bottom, black 0, black calc(100% - 40px), transparent 100%)',
  maxHeight: 'min(560px, calc(100vh - 320px))',
  overflow: 'hidden',
}

export function WeekGrid({
  days,
  items,
  optimisticDrafts,
  openingId,
  onOpenPost,
  onEdit,
  onUnschedule,
  onAddPost,
  onApprove,
  onDelete,
  className,
}: WeekGridProps) {
  const navigate = useNavigate()

  const itemsById = useMemo(() => new Map(items.map(item => [item.creativeId, item])), [items])

  // Per-day labels precomputed in one pass so we don't re-allocate Date /
  // call toLocaleDateString twice per cell on every render.
  const dayLabels = useMemo(() => {
    const todayUtc = new Date()
    todayUtc.setUTCHours(0, 0, 0, 0)
    const todayKey = todayUtc.toISOString().slice(0, 10)
    return days.map(day => {
      const d = new Date(day.date)
      return {
        weekdayShort: d
          .toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })
          .toUpperCase(),
        dateLabel: d.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        }),
        isToday: day.date.slice(0, 10) === todayKey,
      }
    })
  }, [days])

  return (
    <div className={cn('relative border-x border-line', className)} style={MASK_STYLE}>
      <div className="grid grid-cols-7 border-y border-line">
        {days.map((day, i) => (
          <div
            key={`wkd-${day.date}`}
            className={cn(
              'mono border-r border-line px-2 py-2 text-center text-[10px] tracking-[0.18em] uppercase last:border-r-0',
              dayLabels[i].isToday ? 'text-primary' : 'text-paper-faint'
            )}
          >
            {dayLabels[i].weekdayShort}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {days.map((day, i) => {
          const dayItems = day.itemIds
            .map(id => itemsById.get(id))
            .filter((it): it is WeeklyQueueItem => Boolean(it))
            // Surface posts that need approval first so the Approve CTA always
            // rides to the top of the day's stack (and is the visible card) —
            // never buried under the "+N more" chip behind an already-scheduled
            // post. Within each group, sort chronologically so the visible card
            // reflects the earliest post; items without a scheduledAt (rare —
            // autopilot slots before draft lands) sink to the end.
            .sort((a, b) => {
              const aNeeds = a.status === 'needs_approval' ? 0 : 1
              const bNeeds = b.status === 'needs_approval' ? 0 : 1
              if (aNeeds !== bNeeds) return aNeeds - bNeeds
              const aT = a.scheduledAt ? Date.parse(a.scheduledAt) : Number.POSITIVE_INFINITY
              const bT = b.scheduledAt ? Date.parse(b.scheduledAt) : Number.POSITIVE_INFINITY
              return aT - bT
            })
          const first = dayItems[0]
          const extra = Math.max(0, dayItems.length - 1)
          const { dateLabel, isToday } = dayLabels[i]
          const optimisticPending = (optimisticDrafts[day.date] ?? 0) > 0
          const expectedButEmpty = !first && (day.expectedCount > 0 || optimisticPending)

          return (
            <div
              key={day.date}
              data-testid="week-cell"
              data-day={day.date}
              data-status={first?.status ?? 'empty'}
              className="group/cell flex min-w-0 flex-col border-r border-b border-line bg-bg-2 last:border-r-0"
            >
              <div className="border-b border-line px-3 py-2 text-center">
                <p
                  className={cn('text-[13px] font-medium', isToday ? 'text-primary' : 'text-paper')}
                >
                  {dateLabel}
                </p>
              </div>

              <div className="flex flex-1 flex-col gap-2 p-2">
                {first ? (
                  <QueuedPostCard
                    item={first}
                    opening={openingId === first.creativeId}
                    onOpenPost={() => onOpenPost(first.creativeId)}
                    onEdit={() => onEdit(first.creativeId, first.channel)}
                    onUnschedule={() => onUnschedule(first.creativeId)}
                    onApprove={onApprove ? () => onApprove(first.creativeId) : undefined}
                    onDelete={() => onDelete?.(first.creativeId, first.channel)}
                  />
                ) : (
                  <EmptyDayPlaceholder
                    generating={expectedButEmpty}
                    onAddPost={() => onAddPost(day.date)}
                  />
                )}

                {extra > 0 && (
                  <button
                    type="button"
                    onClick={() => navigate('/calendar')}
                    className="inline-flex w-full items-center justify-center gap-1 rounded-[8px] border border-line bg-bg-3 px-2 py-1 text-[12px] font-medium text-paper-dim transition-colors hover:border-line-bright hover:bg-bg-4 hover:text-paper"
                  >
                    +{extra} more
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function EmptyDayPlaceholder({
  generating,
  onAddPost,
}: {
  generating: boolean
  onAddPost: () => Promise<void> | void
}) {
  const [adding, setAdding] = useState(false)
  // Parent (WeeklyCockpit) refetches after addPost, which can swap this
  // placeholder out for a real QueuedPostCard before the await resolves.
  // Guard the trailing setAdding(false) so we don't write into a stale tree.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  if (generating) {
    return (
      <div className="flex aspect-[4/5] w-full animate-pulse items-center justify-center rounded-[12px] border border-dashed border-line bg-bg-3/30">
        <span className="mono flex items-center gap-1.5 text-[10px] tracking-[0.1em] text-paper-faint uppercase">
          <Loader2 size={12} className="animate-spin" />
          Drafting
        </span>
      </div>
    )
  }

  const handleClick = async () => {
    if (adding) return
    setAdding(true)
    try {
      await onAddPost()
    } finally {
      if (mountedRef.current) setAdding(false)
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={adding}
      className={cn(
        'group flex aspect-[4/5] w-full flex-col items-center justify-center gap-1.5 rounded-[12px] border border-dashed border-line bg-bg-3/30 transition-[opacity,background-color,border-color] hover:border-acid/60 hover:bg-bg-3',
        'md:opacity-0 md:group-hover/cell:opacity-100 md:focus-visible:opacity-100',
        adding && 'opacity-60'
      )}
    >
      {adding ? (
        <Loader2 size={16} className="animate-spin text-paper-faint" />
      ) : (
        <Plus size={16} className="text-paper-faint transition-colors group-hover:text-acid" />
      )}
      <span className="mono text-[10px] tracking-[0.1em] text-paper-faint uppercase transition-colors group-hover:text-paper">
        Add a post
      </span>
    </button>
  )
}
